// src/App.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography, Paper } from '@mui/material';
import theme from './theme';
import SuccessDialog from './components/SuccessDialog';
import useAppState from './domain/store';
import TYI_ICON_LOCKUP_WHITE from './assets/images/TYI_ICON_LOCKUP_WHITE.png';

const App: React.FC = () => {
  const { userPassword, setUserPassword, loading } = useAppState();
  const [dialogOpen, setDialogOpen] = useState(!!userPassword);
  const [redirectStatus, setRedirectStatus] = useState(
    'Identifying your environment...'
  );
  const [redirectProgress, setRedirectProgress] = useState(0);
  const [deviceType, setDeviceType] = useState<
    'android' | 'ios' | 'desktop' | null
  >(null);

  // Detect device type
  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/android/i.test(userAgent)) {
      setDeviceType('android');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      setDeviceType('ios');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setUserPassword(null); // Clear userPassword after showing dialog
  };

  useEffect(() => {
    if (userPassword) {
      setDialogOpen(true);
    }
  }, [userPassword]);

  const handleDownloadApp = useCallback(() => {
    // Always redirect to store/website, regardless of previous visits

    // Simulate a progressive redirect
    const startRedirect = async () => {
      // Start with detecting environment
      setRedirectStatus('Identifying your environment...');

      // Wait 1 second with progress updates
      for (let i = 0; i <= 100; i += 5) {
        setRedirectProgress(i);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Show preparing redirect message
      setRedirectStatus('Preparing redirect...');
      setRedirectProgress(0);

      // Wait another second with progress updates
      for (let i = 0; i <= 100; i += 5) {
        setRedirectProgress(i);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Final message before redirect
      setRedirectStatus('Redirecting to best experience...');

      // Wait a bit before actual redirect
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Perform actual redirect
      if (deviceType === 'android') {
        setRedirectStatus('Redirecting to Google Play Store...');
        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.href =
          'https://play.google.com/store/apps/details?id=com.e2g.trackyourimpact.v2';
      } else if (deviceType === 'ios') {
        setRedirectStatus('Redirecting to App Store...');
        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.href =
          'https://apps.apple.com/us/app/trackyourimpact/id6738114232';
      } else {
        setRedirectStatus('Redirecting to website...');
        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.href = 'https://www.e2gfood.com';
      }
    };

    startRedirect();
  }, [deviceType]);

  useEffect(() => {
    // Call handleDownloadApp immediately when the component loads
    handleDownloadApp();
  }, [handleDownloadApp]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: theme.palette.action.hover, // Use theme background color
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 3,
            borderRadius: 3,
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
            backgroundColor: theme.palette.background.default, // Use theme paper background
            boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <img
              src={TYI_ICON_LOCKUP_WHITE}
              alt="Track Your Impact"
              style={{ height: '40px', maxWidth: '100%' }}
            />
          </Box>

          <Box
            sx={{
              my: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 3,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            }}
          >
            <CircularProgress
              variant={redirectProgress > 0 ? 'determinate' : 'indeterminate'}
              value={redirectProgress}
              color="primary"
              size={60}
              thickness={4}
              sx={{ color: theme.palette.primary.main }} // Use theme primary color
            />

            <Typography
              variant="body1"
              sx={{ mt: 3, mb: 1, color: theme.palette.primary.main }}
            >
              {redirectStatus}
            </Typography>

            {redirectProgress > 0 && (
              <Typography variant="body2" color="text.secondary">
                {redirectProgress}%
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.primary" sx={{ mt: 2 }}>
            Please wait while we direct you to the best experience for your
            device.
          </Typography>
        </Paper>
      </Box>

      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress color="primary" />
        </div>
      )}
      <SuccessDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onDownloadApp={handleDownloadApp}
      />
    </ThemeProvider>
  );
};

export default App;
